import { engineComponent } from "neo-common";
import { Paylines } from "../Paylines";
import { PaylinesSelector } from "../PaylinesSelector";
import { Symbols } from "../Symbols";
import { WinAnimator } from "../WinAnimator";
import { Machine } from "../Machine";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { GameManager } from "../../Managers/GameManager";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../Utils/FrameTimer";
import { WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { FreespinsController } from "./FreespinsController";
import { BackgroundStripElement } from "../../CustomComponents/BackgroundStripElement";
import { ModuleManager } from "../../Managers/ModuleManager";
import { HtmlController } from "../../HTMLComponents/HtmlController";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { Tweens } from "../../Components/Effects/Tweens";
import { TeasingBonus } from "../../CustomComponents/TeasingBonus";
import { PlayList } from "../../Components/Sounds/PlayList";
import { DataManager } from "../../Managers/DataManager";
import { TweenLite } from "gsap";
import { ImageElement } from "../../Components/Elements/Graphics/ImageElement";
import { StageController } from "../../Components/StageController";
import { AmbientBackground } from "../../Utils/AmbientBackground";

@engineComponent(ComponentTypes.DisplayObject)
export class FreespinsView extends ContainerElement {
    private _controller: FreespinsController;
    private _outroDelay: FrameTimerEventEmitter;
    private _fsMessageEndDelay: FrameTimerEventEmitter;
    private _fsMessageStartDelay: FrameTimerEventEmitter;
    private _fsMessageEndSoundDelay: FrameTimerEventEmitter;
    private _fsMessageStartSoundDelay: FrameTimerEventEmitter;

    private _paylines: Paylines;
    private _paylinesSelector: PaylinesSelector;

    private _symbols: Symbols;
    private _machine: Machine;
    private _noWinContainer: ContainerElement;
    private _winAnimator: WinAnimatorExtended;

    private _fsMessageStart: HTMLElement;
    private _fsMessageEnd: HTMLElement;
    private _fsMessageEndNoWin: HTMLElement;

    /* Switch elements animated during win (and others?) */
    private _actionElements: Array<SwitchElement> = [];

    private _dice: Array<SwitchElement> = [];
    private _diceCount: number;

    private _teasingBonus: TeasingBonus;

    private _crissCross: ContainerElement;

    private _background: SwitchElement;
    private _reelBorder: SwitchElement;

    private _foreground: SwitchElement;
    private _foregroundUnder: SwitchElement;

    private _curtain: SwitchElement;
    private _diceLight: SwitchElement;

    private _fsConfigurator: HTMLElement;
    private _diceObjects: Array<HTMLElement>;

    private _transition: SwitchElement;

    constructor() {
        super();

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        this._fsMessageStartDelay = CreateFrameTimer("fsMessageStartDelay");
        this._fsMessageStartDelay.on(FrameTimerEvents.Complete, this.fsMessageStart.bind(this));

        this._fsMessageEndDelay = CreateFrameTimer("fsMessageEndDelay");
        this._fsMessageEndDelay.on(FrameTimerEvents.Complete, this.fsMessageEnd.bind(this));

        this._fsMessageEndSoundDelay = CreateFrameTimer("fsMessageEndSoundDelay");
        this._fsMessageEndSoundDelay.on(FrameTimerEvents.Complete, this.fsMessageEndSoundDelay.bind(this));

        this._fsMessageStartSoundDelay = CreateFrameTimer("fsMessageStartSoundDelay");
        this._fsMessageStartSoundDelay.on(FrameTimerEvents.Complete, this.fsMessageStartSoundDelay.bind(this));

        this._teasingBonus = new TeasingBonus();

        this._fsConfigurator = document.getElementById("fsConfigurator");
        this._diceObjects = [document.getElementById("dice-1-object"), document.getElementById("dice-2-object")];
    }

    createByNode(node: Element): void {
        super.createByNode(node);

        let actionElementNames: Array<string> = [];

        if (node.hasAttribute(FreespinsViewAttributes.ACTION_ELEMENTS)) {
            actionElementNames = node.getAttribute(FreespinsViewAttributes.ACTION_ELEMENTS).split(";");
        }

        if (this.childs != null) {
            for (var key in this.childs) {
                for (let i = 0; i < actionElementNames.length; i++) {
                    if (key == actionElementNames[i] && this.childs[key] instanceof SwitchElement) {
                        this._actionElements.push(this.childs[key]);
                    }
                }

                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                }

                if (this.childs[key] instanceof PaylinesSelector) {
                    this._paylinesSelector = this.childs[key];
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof Machine) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimator) {
                    this._winAnimator = this.childs[key];
                }

                if (key === "NoWinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._noWinContainer = this.childs[key];
                }

                if (key === "TeasingFront" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ teasingFront: this.childs[key] });
                    }
                }

                if (key === "TeasingParticles" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ teasingParticles: this.childs[key] });
                    }
                }

                if (key === "CrissCross_FS" && this.childs[key] instanceof ContainerElement) {
                    this._crissCross = this.childs[key];
                }

                if (key === "BackgroundFs" && this.childs[key] instanceof SwitchElement) {
                    this._background = this.childs[key];
                }

                if (key === "ReelBorderFs" && this.childs[key] instanceof SwitchElement) {
                    this._reelBorder = this.childs[key];
                }

                if (key === "ForegroundOver" && this.childs[key] instanceof SwitchElement) {
                    this._foreground = this.childs[key];
                }
                if (key === "ForegroundUnder" && this.childs[key] instanceof SwitchElement) {
                    this._foregroundUnder = this.childs[key];
                }

                if (key === "CurtainFs" && this.childs[key] instanceof SwitchElement) {
                    this._curtain = this.childs[key];
                }

                if (key === "DiceLight" && this.childs[key] instanceof SwitchElement) {
                    this._diceLight = this.childs[key];
                }

                if (key === "Transition" && this.childs[key] instanceof SwitchElement) {
                    this._transition = this.childs[key];
                }
            }

            this._fsMessageStart = document.getElementById("fsMessageStart");
            if (this._fsMessageStart == null) {
                if (DEBUG) {
                    this.log("ERROR no fsMessageStart");
                }
            }

            this._fsMessageEnd = document.getElementById("fsMessageEnd");
            if (this._fsMessageEnd == null) {
                if (DEBUG) {
                    this.log("ERROR no fsMessageEnd");
                }
            }

            this._fsMessageEndNoWin = document.getElementById("fsMessageEndNoWin");
            if (this._fsMessageEndNoWin == null) {
                if (DEBUG) {
                    this.log("ERROR no fsMessageEndNoWin");
                }
            }

            if (this._symbols == null) {
                if (DEBUG) {
                    this.log("ERROR no symbols");
                }
            }

            if (this._machine == null) {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }

            if (this._paylines == null) {
                if (DEBUG) {
                    this.log("WARNING no paylines");
                }
            }

            if (this._winAnimator == null) {
                if (DEBUG) {
                    this.log("WARNING no winAnimator");
                }
            }
        }
    }

    initReceived(controller: FreespinsController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.freespinsResponse != null) {
                    if (
                        GameManager.freespinsResponse.initData != null &&
                        GameManager.freespinsResponse.initData.reels !== null
                    ) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.initialize(this._symbols, GameManager.freespinsResponse.initData.reels);
                        this._machine.addElementInstance({ teasing: [this._teasingBonus] });
                    }

                    if (this._winAnimator != null) {
                        this._winAnimator.on(WinAnimator.FinishWinCalculation, this.finishWinCalculation.bind(this));
                        this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                        this._winAnimator.on(
                            WinAnimator.FinishQuickWinCalculation,
                            this.finishQuickWinCalculation.bind(this),
                        );
                        this._winAnimator.initialize(this._symbols, this._paylines, this._noWinContainer);
                        this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
                        this._winAnimator.addElementInstance({ teasing: [this._teasingBonus] });
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no freespin reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }

        this.visible = false;

        if (
            ModuleManager.freespinsEnabled() &&
            GameManager.freespinsResponse.customData &&
            Array.isArray(GameManager.freespinsResponse.customData.dice)
        ) {
            GameManager.freespinsResponse.customData.dice.forEach((value: number, index: number) => {
                this._diceObjects[index].classList.add("init");
            });
            this._fsMessageStartDelay.start(120);
        }
    }

    disableLogo(): void {
        if (this.childs["Logo"] != null) {
            this.removeChild(this.childs["Logo"]);
        }
    }

    initIntro(): void {
        this._machine.setStoppedSymbolsArray(GameManager.freespinsResponse.screen.reels);
        this._winAnimator.showSymbols(GameManager.freespinsResponse.screen.reels);
        this._winAnimator.setCustomData(GameManager.freespinsResponse.customData);
        this._winAnimator.showInitWins(GameManager.freespinsResponse.wins, GameManager.freespinsResponse.currentWin);

        this.visible = true;

        AmbientBackground.changeAmbientBackground("color-1", 0);

        if (this.isLastFreespin()) {
            PlayList.stop("SND_FSLoop");
            PlayList.loop("SND_FSEnd");

            if (GameManager.freespinsResponse.totalWin > 0) {
                if (this._fsMessageEnd != null && this._fsMessageEnd.classList.contains(HtmlController.DisabledClass)) {
                    this._fsMessageEnd.classList.remove(HtmlController.DisabledClass);
                }
            } else {
                if (
                    this._fsMessageEndNoWin != null &&
                    this._fsMessageEndNoWin.classList.contains(HtmlController.DisabledClass)
                ) {
                    this._fsMessageEndNoWin.classList.remove(HtmlController.DisabledClass);
                }
            }
        } else {
            PlayList.loop("SND_FSLoop");
        }
    }

    spin(): void {
        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }
        this._machine.spinStart(this._winAnimator.reset(false, true));

        if (this._fsMessageStart != null && !this._fsMessageStart.classList.contains(HtmlController.DisabledClass)) {
            this._fsMessageStart.classList.add(HtmlController.DisabledClass);
        }
        if (this._fsConfigurator != null && !this._fsConfigurator.classList.contains(HtmlController.DisabledClass)) {
            this._fsConfigurator.classList.add(HtmlController.DisabledClass);
            this._fsConfigurator.classList.remove("show");
        }
        for (let dice of this._diceObjects) {
            dice.className = "";
        }
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    startReceived(): boolean {
        StageController.resumeAnimations();
        this._machine.setStoppedSymbolsArray(GameManager.freespinsResponse.screen.reels);
        this._winAnimator.showSymbols(GameManager.freespinsResponse.screen.reels);
        this._background.goToCase("DICE_OUT");
        this._reelBorder.goToCase("DICE_OUT");
        this._curtain.goToCase("DICE_OUT");
        this._diceLight.goToCase("DICE_OUT");

        if (this._transition != null) {
            this._transition.goToCase("FS_INIT");
        }

        this._winAnimator.alpha = 1;
        //(<ImageElement>this._background.getChildElementByName("ReelsBorder")).position.y = 1;
        this._reelBorder.position.y = 1;
        this._crissCross.position.y = 0;
        this._winAnimator.position.y = 0;
        this._foreground.position.y = 0;
        this._foregroundUnder.position.y = 0;

        Tweens.alphaTween(this._winAnimator, {
            alphas: [0, 1],
            tweenDelay: 60,
            tweenDuration: 30,
        });

        Tweens.alphaTween(this._foreground, {
            alphas: [0, 1],
            tweenDelay: 60,
            tweenDuration: 30,
        });

        Tweens.alphaTween(this._foregroundUnder, {
            alphas: [0, 1],
            tweenDelay: 60,
            tweenDuration: 30,
        });

        Tweens.alphaTween(this._crissCross, {
            alphas: [0, 1],
            tweenDelay: 60,
            tweenDuration: 30,
        });

        /*
        Tweens.positionTween(this._winAnimator, {
            tweenX: [0, 0],
            tweenY: [-995, 0],
            tweenDuration: 30,
            tweenEase: 'sineOut'
        });
        this._winAnimator.alpha = 1;

        Tweens.positionTween(this._foreground, {
            tweenX: [0, 0],
            tweenY: [-995, 0],
            tweenDuration: 30,
            tweenEase: 'sineOut'
        });

        Tweens.positionTween(this._crissCross, {
            tweenX: [0, 0],
            tweenY: [-995, 0],
            tweenDuration: 30,
            tweenEase: 'sineOut'
        });
        */

        if (this._fsMessageStart != null && !this._fsMessageStart.classList.contains(HtmlController.DisabledClass)) {
            this._fsMessageStart.classList.add(HtmlController.DisabledClass);
        }

        if (this._fsConfigurator != null && !this._fsConfigurator.classList.contains(HtmlController.DisabledClass)) {
            this._fsConfigurator.classList.add(HtmlController.DisabledClass);
            this._fsConfigurator.classList.remove("show");
        }

        for (let dice of this._diceObjects) {
            dice.className = "";
        }

        this._fsMessageStartSoundDelay.stop();
        PlayList.stop("SND_FSStartDice", 30);
        // PlayList.stop('SND_FSTransition', 30);

        return true;
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.freespinsResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    endReceived(): void {
        this._fsMessageEndSoundDelay.stop();
        PlayList.stop("SND_FSEnd", 60);
        // PlayList.play('SND_FSTransition');
        //this.outro();
    }

    onReelStopped(): void {
        this._winAnimator.onReelStopped(
            GameManager.freespinsResponse.screen.reels,
            this._machine.actualReelStopped,
            this._machine.forceStop,
        );
    }

    machineStopped(): void {
        this._controller.machineStopped();
        if (GameManager.freespinsResponse.hasOwnProperty("customData")) {
            this._winAnimator.setCustomData(GameManager.freespinsResponse.customData);
        }

        this._winAnimator.showWins(GameManager.freespinsResponse.wins, GameManager.freespinsResponse.currentWin);

        if (
            GameManager.freespinsResponse.wins &&
            GameManager.freespinsResponse.wins.length &&
            GameManager.freespinsResponse.currentWin
        ) {
            if (this._winAnimator.getCurrentMultiplierIndex() > 1) {
                this.setActionElementsToState("HIGH_WIN_" + (this._winAnimator.getCurrentMultiplierIndex() - 1));
            } else {
                this.setActionElementsToState("WIN");
            }
        } else {
            this.setActionElementsToState("NORMAL");
        }

        if (GameManager.freespinsResponse.currentWin === 0) {
            if (this.isLastFreespin()) {
                PlayList.stop("SND_FSLoop");
                PlayList.loop("SND_FSEnd");

                if (GameManager.freespinsResponse.totalWin > 0) {
                    if (
                        this._fsMessageEnd != null &&
                        this._fsMessageEnd.classList.contains(HtmlController.DisabledClass)
                    ) {
                        this._fsMessageEnd.classList.remove(HtmlController.DisabledClass);
                    }
                } else {
                    if (
                        this._fsMessageEndNoWin != null &&
                        this._fsMessageEndNoWin.classList.contains(HtmlController.DisabledClass)
                    ) {
                        this._fsMessageEndNoWin.classList.remove(HtmlController.DisabledClass);
                    }
                }
            }
        }
    }

    private finishQuickWinCalculation(): void {
        this._controller.quickWinCalculationFinished();
    }

    finishWinCalculation(): void {
        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        this.setActionElementsToState("NORMAL_QUICK");
        this._controller.allWinsFinished();

        if (this.isLastFreespin()) {
            // PlayList.stop('SND_FSBKGLoopGreen', 10);
            // PlayList.stop('SND_FSBKGLoopBlue', 10);
            // PlayList.stop('SND_FSBKGLoopWhite', 10);
            // PlayList.stop('SND_FSBKGLoopRed', 10);
            // PlayList.stop('SND_FSBKGLoopGold', 10);

            // PlayList.play('SND_FSEnd');
            PlayList.stop("SND_FSLoop", 60);

            this.fsMessageEnd();

            // this._background.goToCase('FS_OUT_TABLE');
            // this._curtain.goToCase('FS_OUT_TABLE');
            // this._winAnimator.reset(true);
            // Tweens.positionTween(this._winAnimator, {
            //     tweenX: [0, 0],
            //     tweenY: [0, -995],
            //     tweenDuration: 30,
            //     tweenEase: 'sineIn'
            // });
            //
            // Tweens.positionTween(this._crissCross, {
            //     tweenX: [0, 0],
            //     tweenY: [0, -995],
            //     tweenDuration: 30,
            //     tweenEase: 'sineIn'
            // });
            //
            // this._foreground.alpha = 1;
            // Tweens.positionTween(this._foreground, {
            //     tweenX: [0, 0],
            //     tweenY: [0, -995],
            //     tweenDuration: 30,
            //     tweenEase: 'sineIn'
            // });
            //
            // this._fsMessageEndDelay.start(90);
        }
    }

    fsMessageStart(): void {
        this._fsMessageStartDelay.stop();
        // PlayList.stop('SND_BaseRecaploop', 30);
        PlayList.play("SND_FSMessage");

        this._fsMessageStartSoundDelay.start(120);
        this._diceLight.goToCase("DICE_IN");

        if (this._fsMessageStart != null && this._fsMessageStart.classList.contains(HtmlController.DisabledClass)) {
            this._fsMessageStart.classList.remove(HtmlController.DisabledClass);
        }
        setTimeout(function () {
            ModuleManager.freeSpinsStartButton();
        }, 500);
    }

    private fsMessageStartSoundDelay(): void {
        // PlayList.loop('SND_FSEndLoop', 30);
    }

    fsMessageEnd(): void {
        PlayList.play("SND_FSMessage");
        this._fsMessageEndSoundDelay.start(90);

        if (GameManager.freespinsResponse.totalWin > 0) {
            if (this._fsMessageEnd != null && this._fsMessageEnd.classList.contains(HtmlController.DisabledClass)) {
                this._fsMessageEnd.classList.remove(HtmlController.DisabledClass);
            }
        } else {
            if (
                this._fsMessageEndNoWin != null &&
                this._fsMessageEndNoWin.classList.contains(HtmlController.DisabledClass)
            ) {
                this._fsMessageEndNoWin.classList.remove(HtmlController.DisabledClass);
            }
        }
    }

    private fsMessageEndSoundDelay(): void {
        PlayList.loop("SND_FSEnd", 30);
    }

    intro(long: boolean = false): void {
        if (this._winAnimator != null) {
            this._winAnimator.visible = true;
            // this._winAnimator.alpha = 1;
            // Tweens.alphaTween(this._winAnimator, {
            //     alphas: [0, 1]
            // });
        }

        // if (this._transition != null) {
        //     this._transition.goToCase('FS_INTRO');
        // }

        this.introFinished();
    }
    introFinished(): void {
        this._controller.introFinished();
    }

    outro(): void {
        AmbientBackground.changeAmbientBackground("base", 90);

        if (this._fsMessageEnd != null && !this._fsMessageEnd.classList.contains(HtmlController.DisabledClass)) {
            this._fsMessageEnd.classList.add(HtmlController.DisabledClass);
        }

        if (
            this._fsMessageEndNoWin != null &&
            !this._fsMessageEndNoWin.classList.contains(HtmlController.DisabledClass)
        ) {
            this._fsMessageEndNoWin.classList.add(HtmlController.DisabledClass);
        }

        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }
        this._winAnimator.reset(true);

        if (this._transition != null) {
            this._transition.goToCase("FS_OUTRO");
        }

        if (ModuleManager.freespinsEnabled()) {
            this._outroDelay.start(60);
        } else {
            // PlayList.stop('SND_FSEndLoop');
            // PlayList.play('SND_FSOutro');

            this._outroDelay.start(120);
        }
    }

    outroFinished(): void {
        this._outroDelay.stop();

        this.visible = false;
        this._controller.outroFinished();
    }

    enable(): void {
        this._controller.startConfigurator();

        this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
        this._background.goToCase("DICE_IN");
        this._reelBorder.goToCase("DICE_IN");
        this._curtain.goToCase("DICE_IN");
        this._transition.goToCase("NORMAL");

        this._foreground.alpha = 1;
        this._foregroundUnder.alpha = 1;
        Tweens.positionTween(this._foreground, {
            tweenX: [0, 0],
            tweenY: [0, -995],
            tweenDuration: 30,
            tweenEase: "sineIn",
        });
        Tweens.positionTween(this._foregroundUnder, {
            tweenX: [0, 0],
            tweenY: [0, -995],
            tweenDuration: 30,
            tweenEase: "sineIn",
        });

        this._winAnimator.alpha = 1;
        this._winAnimator.visible = true;
        Tweens.positionTween(this._winAnimator, {
            tweenX: [0, 0],
            tweenY: [0, -995],
            tweenDuration: 30,
            tweenEase: "sineIn",
        });
        this.visible = true;

        Tweens.positionTween(this._crissCross, {
            tweenX: [0, 0],
            tweenY: [0, -995],
            tweenDuration: 30,
            tweenEase: "sineIn",
        });

        this._background.once(SwitchElement.AnimationComplete, () => {
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_FSStartDice");
            PlayList.stop("SND_BaseRecaploop");
            this._fsConfigurator.classList.remove("_disabled");
            this._fsConfigurator.classList.add("show");
            if (
                ModuleManager.freespinsEnabled() &&
                GameManager.freespinsResponse.customData &&
                Array.isArray(GameManager.freespinsResponse.customData.dice)
            ) {
                let promises: Array<Promise<any>> = [];
                GameManager.freespinsResponse.customData.dice.forEach((value: number, index: number) => {
                    promises.push(
                        new Promise((resolve: any, reject: any) => {
                            this._diceObjects[index].classList.add("roll" + value);
                            this._diceObjects[index].addEventListener(
                                "animationend",
                                this.animationEndListener.bind(this, resolve, index),
                            );
                            this._diceObjects[index].addEventListener(
                                "animationstart",
                                this.animationStartListener.bind(this, index),
                            );
                        }),
                    );
                });

                Promise.all(promises).then(() => {
                    StageController.resumeAnimations();
                    this._fsMessageStartDelay.start(120);
                });
            }
        });
    }

    private animationEndListener(resolve: any, index: number, e: AnimationEvent): void {
        if (e.animationName.match(/^roll\d/)) {
            PlayList.play("SND_FSDiceStop");
            resolve();
            this._diceObjects[index].removeEventListener(
                "animationend",
                this.animationEndListener.bind(this, resolve, index),
            );
        }
    }

    private animationStartListener(index: number, e: AnimationEvent): void {
        if (e.animationName.match(/^roll\d/)) {
            StageController.pauseAnimations();
        }
        if (e.animationName.substring(0, "bounce".length) === "bounce") {
            PlayList.play("SND_FSDiceZoom");
        }
        this._diceObjects[index].removeEventListener("animationstart", this.animationStartListener.bind(this, index));
    }

    private setActionElementsToState(state: string): void {
        this._actionElements.forEach((element) => {
            if (element.hasCase(state) && element.getCurrentCase() != state) {
                element.goToCase(state);
            }
        });
    }

    startConfigurator(): void {
        if (
            ModuleManager.freespinsEnabled() &&
            GameManager.freespinsResponse.customData &&
            Array.isArray(GameManager.freespinsResponse.customData.dice)
        ) {
            this._fsConfigurator.setAttribute("data-dice-count", GameManager.freespinsResponse.customData.dice.length);
        }
    }

    /**
     * Checks last freespin.
     * @returns {boolean}
     */
    private isLastFreespin(): boolean {
        if (DataManager.GameLimit) {
            return true;
        }

        if (
            GameManager.freespinsResponse.freeSpins != null &&
            GameManager.freespinsResponse.freeSpins.remainingCount != null &&
            GameManager.freespinsResponse.freeSpins.remainingCount === 0
        ) {
            return true;
        }

        return false;
    }
}

export const FreespinsViewAttributes = {
    ACTION_ELEMENTS: "ActionElements",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Freespins/FreespinsView.ts
 **/